/**
 * Minimalerei Announcement Bar — Phase 3.A
 *
 * Custom <announcement-bar> element. Cross-fade slideshow with hover/focus
 * pause, touch-tap pause (5s grace), keyboard nav, reduced-motion support.
 *
 * Pause-state is reason-tracked: hover, focus, touch, manual-toggle each
 * add to a Set. Bar resumes only when all reasons clear. This avoids the
 * "mouse-leaves-but-still-paused-from-toggle" bugs and keeps logic explicit.
 *
 * Timer math: pauseTimerInternal() captures elapsed time so resumeTimer-
 * Internal() continues with remaining duration — pause doesn't lose progress.
 * CSS animation-play-state: paused does the same for the progress indicator,
 * so both stay in sync.
 *
 * Hard floor of 3000ms speed to prevent epileptic flicker if a setting goes
 * out-of-range somehow.
 */

class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-announcement-item]'));
    if (this.items.length === 0) return;

    this.progressEl = this.querySelector('[data-announcement-progress]');
    this.prevBtn = this.querySelector('[data-announcement-prev]');
    this.nextBtn = this.querySelector('[data-announcement-next]');
    this.liveRegion = this.querySelector('[data-announcement-live]');
    this.pauseToggle = this.querySelector('[data-announcement-pause-toggle]');

    const speedMs = parseInt(this.dataset.speed || '5000', 10);
    this.speed = Math.max(3000, isNaN(speedMs) ? 5000 : speedMs);

    this.currentIndex = 0;
    this.timer = null;
    this.timerStart = null;
    this.timerRemaining = this.speed;
    this.touchPauseTimer = null;
    this.pauseReasons = new Set();

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.autoAdvanceEnabled = !this.reducedMotion.matches && this.items.length > 1;

    this.activate(0, { silent: true });
    this.bindEvents();

    if (this.autoAdvanceEnabled) {
      this.startTimer();
    }

    // React to live changes in motion preference (e.g. user toggles in OS)
    this.reducedMotion.addEventListener('change', (e) => {
      this.autoAdvanceEnabled = !e.matches && this.items.length > 1;
      if (this.autoAdvanceEnabled) {
        this.startTimer();
      } else {
        this.pauseTimerInternal();
      }
    });
  }

  bindEvents() {
    // Hover pause (mouse only — pointerType filter)
    this.addEventListener('pointerenter', (e) => {
      if (e.pointerType !== 'mouse') return;
      clearTimeout(this.touchPauseTimer);
      this.addPauseReason('hover');
    });
    this.addEventListener('pointerleave', (e) => {
      if (e.pointerType !== 'mouse') return;
      this.removePauseReason('hover');
    });

    // Touch tap pause: 5s grace, then auto-resume
    this.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'touch') return;
      this.addPauseReason('touch');
      clearTimeout(this.touchPauseTimer);
      this.touchPauseTimer = setTimeout(() => {
        this.removePauseReason('touch');
      }, 5000);
    });

    // Keyboard focus pause
    this.addEventListener('focusin', () => {
      clearTimeout(this.touchPauseTimer);
      this.addPauseReason('focus');
    });
    this.addEventListener('focusout', (e) => {
      // Only resume when focus leaves the bar entirely
      if (!this.contains(e.relatedTarget)) {
        this.removePauseReason('focus');
      }
    });

    // Arrow buttons
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.navigate(-1, { announce: true }));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.navigate(1, { announce: true }));
    }

    // Keyboard arrow keys (when bar or descendants have focus)
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.navigate(-1, { announce: true });
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.navigate(1, { announce: true });
      }
    });

    // Pause toggle (visually-hidden but Tab-reachable)
    if (this.pauseToggle) {
      this.pauseToggle.addEventListener('click', () => {
        const wasPaused = this.pauseToggle.getAttribute('aria-pressed') === 'true';
        if (wasPaused) {
          this.pauseToggle.setAttribute('aria-pressed', 'false');
          this.pauseToggle.textContent = 'Pausieren';
          this.removePauseReason('manual');
        } else {
          this.pauseToggle.setAttribute('aria-pressed', 'true');
          this.pauseToggle.textContent = 'Fortsetzen';
          this.addPauseReason('manual');
        }
      });
    }
  }

  /** Mark `index` as the active item. silent=true skips live-region update. */
  activate(index, { silent = false } = {}) {
    this.items.forEach((item, i) => {
      const isActive = i === index;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-hidden', String(!isActive));
      // Trap inner focusables — only the active item is reachable via Tab
      item.querySelectorAll('a, button').forEach((el) => {
        el.setAttribute('tabindex', isActive ? '0' : '-1');
      });
    });
    this.currentIndex = index;
    this.restartProgress();

    if (!silent && this.liveRegion) {
      const textEl = this.items[index].querySelector('[data-announcement-text]');
      this.liveRegion.textContent = textEl ? textEl.textContent.trim() : '';
    }
  }

  restartProgress() {
    if (!this.progressEl || !this.autoAdvanceEnabled) return;
    // Web Animations API instead of CSS @keyframes — gives us a direct
    // handle on the animation (cancel/pause/play) and bypasses the
    // CSS-cascade quirks we hit with the class-toggle approach.
    this.progressEl.getAnimations().forEach((a) => a.cancel());
    this.progressAnim = this.progressEl.animate(
      [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }],
      { duration: this.speed, easing: 'linear', fill: 'forwards' }
    );
  }

  /** Manual nav (arrow click / keyboard) — resets timer + announces. */
  navigate(delta, opts = {}) {
    const next = (this.currentIndex + delta + this.items.length) % this.items.length;
    this.activate(next, { silent: !opts.announce });
    if (this.autoAdvanceEnabled && this.pauseReasons.size === 0) {
      this.startTimer();
    }
  }

  /** Begins (or restarts) a fresh full-speed timer for the next advance. */
  startTimer() {
    if (!this.autoAdvanceEnabled) return;
    clearTimeout(this.timer);
    this.timerStart = Date.now();
    this.timerRemaining = this.speed;
    this.timer = setTimeout(() => this.advance(), this.speed);
  }

  /** Auto-advance tick: silent move to next, restart timer. */
  advance() {
    const next = (this.currentIndex + 1) % this.items.length;
    this.activate(next, { silent: true });
    this.startTimer();
  }

  /** Freeze pending advance + progress animation, capture remaining time. */
  pauseTimerInternal() {
    if (!this.timer) return;
    clearTimeout(this.timer);
    const elapsed = Date.now() - this.timerStart;
    this.timerRemaining = Math.max(0, this.timerRemaining - elapsed);
    this.timer = null;
    if (this.progressAnim) this.progressAnim.pause();
  }

  /** Continue with previously-captured remaining time + unfreeze progress. */
  resumeTimerInternal() {
    if (!this.autoAdvanceEnabled) return;
    if (this.timer) return;
    this.timerStart = Date.now();
    this.timer = setTimeout(() => this.advance(), this.timerRemaining);
    if (this.progressAnim) this.progressAnim.play();
  }

  addPauseReason(reason) {
    const wasEmpty = this.pauseReasons.size === 0;
    this.pauseReasons.add(reason);
    if (wasEmpty) {
      this.classList.add('is-paused');
      this.pauseTimerInternal();
    }
  }

  removePauseReason(reason) {
    if (!this.pauseReasons.has(reason)) return;
    this.pauseReasons.delete(reason);
    if (this.pauseReasons.size === 0) {
      this.classList.remove('is-paused');
      this.resumeTimerInternal();
    }
  }
}

if (!customElements.get('announcement-bar')) {
  customElements.define('announcement-bar', AnnouncementBar);
}
